import { Alert, Avatar, Modal } from "antd";
import { useSelector } from "react-redux";
import {
  Delete,
  EditIcon,
  EmailIcon,
  PhoneIcon,
  ModalXIcon,
} from "../../../../assets/icons";
import { useEffect, useMemo, useState } from "react";
import InfoText from "../../../commonComponents/InfoText/InfoText";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { driveApi } from "../../../DriveRequest";
import NewContactModal from "./NewContactModal";
import { API } from "aws-amplify";
import { showErrorMsg } from "../../../Documentation/utils/Messages";
import { useDispatch } from "react-redux";
import { programFields as programFieldsReducer } from "../../../../store/actions";
import { useAuth } from "../../../../authentication/authHelpers";
import Swal from "sweetalert2";
import { getInitials } from "../../../../utils";
import DetailedContactModal, { flag } from "./DetailedContactModal";
import { formatPhoneNumber, getWarningObj } from "./utils";

import "./Contacts.scss";

const COMPANY_CONTACTS_FIELD_ID = "054ebd11-9128-4c78-be38-a0cfabcd3c61";

const ContactsModal = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [{ isDarkMode }, { accessToken }, { programFields }] = useSelector(
    (state) => [state.usersList, state.accessToken, state.programFields]
  );

  const driveRequest = driveApi({ accessToken });
  const isAdmin =
    user?.userRole === "Admin" || user?.userRole === "Super Admin";

  const [tempSrc, setTempSrc] = useState({});
  const [openEditMode, setOpenEditMode] = useState(false);
  const [openDetailedContact, setOpenDetailedContact] = useState(false);

  //region contacts
  const { contacts, pfIdx } = useMemo(() => {
    let tmpPF = structuredClone(programFields);

    if (!tmpPF) return { contacts: [], pfIdx: -1 };

    const pfIdx = tmpPF.findIndex(
      (field) => field.fieldId === COMPANY_CONTACTS_FIELD_ID
    );

    if (pfIdx === -1) return { contacts: [], pfIdx: -1 };

    const contacts = tmpPF[pfIdx].fieldOptions || [];

    return { contacts, pfIdx };
  }, [programFields]);

  //region >confirmDelete
  const confirmDelete = async (contact, cb) => {
    if (!contact || !contact.contactId || pfIdx === -1) {
      showErrorMsg({ content: "Nuk u gjet kontakti për të fshirë." });
      return;
    }

    let newProgramFields = structuredClone(programFields);

    const fieldOptions = (newProgramFields[pfIdx].fieldOptions || []).filter(
      (item) => item.contactId !== contact.contactId
    );

    await API.put(
      "programFields",
      `/programFields/${COMPANY_CONTACTS_FIELD_ID}`,
      { body: { fieldOptions } }
    )
      .then(() => {
        !!cb && cb();

        newProgramFields[pfIdx] = {
          ...newProgramFields[pfIdx],
          fieldOptions,
        };
        dispatch(programFieldsReducer(newProgramFields));
        Swal.fire({
          title: "Kontakti u fshi me sukses!",
          icon: "success",
          customClass: {
            popup: isDarkMode ? "custom-swal-dark" : "custom-swal",
          },
        });
      })
      .catch((err) => {
        console.error("Error deleting contact: ", err);
        Swal.fire({
          title: `Diçka shkoi gabim!`,
          text: `${contact.fullName} nuk u fshi dot. Ju lutem provoni përsëri.`,
          icon: "error",
          customClass: {
            popup: isDarkMode ? "custom-swal-dark" : "custom-swal",
          },
        });
      });
  };

  //region DELETE
  const handleDelete = (e, contact, cb) => {
    e.stopPropagation();
    e.preventDefault();

    Modal.confirm(
      getWarningObj({
        isDarkMode,
        okText: "Fshi",
        cancelText: "Anulo",
        content: "Ky veprim nuk mund të anulohet.",
        onConfirm: () => confirmDelete(contact, cb),
        title: "Jeni i sigurt që doni të fshini këtë kontakt?",
        icon: <Delete width={48} height={48} fill="#EA3943" />,
      })
    );
  };

  //region PFP FETCH
  const handlePfpFetch = async (contacts) => {
    if (!contacts || !contacts.length) return;

    const toFetch = [];
    for (const contact of contacts) {
      const { googleDriveFileId, contactId } = contact;
      if (!tempSrc[contactId] && !!googleDriveFileId) {
        toFetch.push({ googleDriveFileId, contactId });
      }
    }

    const promises = toFetch.map(async ({ googleDriveFileId, contactId }) => {
      const res = await driveRequest.getImageSrc(googleDriveFileId);

      if (!!res?.src) {
        return { [contactId]: res.src };
      }
      return null;
    });

    const results = await Promise.allSettled(promises);

    if (results.length === 0) return;

    const successfulResults = results
      .filter((res) => res.status === "fulfilled" && res.value !== null)
      .map((res) => res.value);

    if (successfulResults.length === 0) return;

    setTempSrc((prev = {}) => ({
      ...prev,
      ...Object.assign({}, ...successfulResults),
    }));
  };

  useEffect(() => {
    if (!accessToken) return;

    handlePfpFetch(contacts);
  }, [contacts, accessToken]);

  useEffect(() => {
    return () => {
      Object.values(tempSrc).forEach((src) => {
        if (src) {
          URL.revokeObjectURL(src);
        }
      });
    };
  }, []);

  //region RETURN
  return (
    <Modal
      centered
      open={open}
      footer={
        <div
          style={{ display: "flex", justifyContent: "center", width: "100%" }}
        >
          <MondayButton
            className="mondayButtonRed"
            onClick={() => setOpen(false)}
          >
            Mbyllni Faqen
          </MondayButton>
        </div>
      }
      title={"Kontaktet e Kompanisë"}
      closeIcon={<ModalXIcon />}
      onCancel={() => setOpen(false)}
      className={`contactsModal ${
        isDarkMode ? "dark darkHeader" : "lightHeader"
      }`}
    >
      <div className="contactsModalContent">
        <div className="contactsModalHeader">
          <InfoText text="Këtu mund të gjeni kontaktet kryesore të kompanisë tonë." />
          {isAdmin ? (
            <MondayButton
              className="mondayButtonGreen"
              onClick={() => setOpenEditMode(true)}
            >
              Krijoni Kontakt
            </MondayButton>
          ) : null}
        </div>
        <div className="contactsList">
          {!!contacts?.length ? (
            contacts.map((contact) => (
              <div
                key={contact.contactId}
                className="contactItem"
                onClick={() =>
                  setOpenDetailedContact({
                    ...contact,
                    src: tempSrc[contact.contactId],
                  })
                }
              >
                <Avatar
                  size={46}
                  shape="round"
                  src={tempSrc[contact.contactId]}
                >
                  {getInitials(contact.fullName)}
                </Avatar>
                <div className="contactItemDetails">
                  <span className="bold">{contact.fullName}</span>
                  <span className="divider" />
                  <span>{contact.department}</span>
                  <span className="divider" />
                  <span>{contact.employeeRoleName}</span>
                </div>
                <div className="contactItemDetails subTitle">
                  <span>
                    {flag(contact.employeeTeam)}
                    {contact.employeeTeam}
                  </span>
                  <span className="divider" />
                  <span>
                    <PhoneIcon />
                    {formatPhoneNumber(contact.employeePhoneNumber)}
                  </span>
                  <span className="divider" />
                  <span>
                    <EmailIcon />
                    {contact.employeeEmail}
                  </span>
                </div>
                {isAdmin ? (
                  <div className="contactItemActions">
                    <EditIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setOpenEditMode(contact);
                      }}
                    />
                    <Delete
                      className="deleteIcon"
                      onClick={(e) => handleDelete(e, contact)}
                    />
                  </div>
                ) : null}
              </div>
            ))
          ) : (
            <Alert
              type="info"
              showIcon
              message="Nuk ka asnjë kontakt të ruajtur"
            />
          )}
        </div>
      </div>
      {!!openEditMode ? (
        <NewContactModal
          open={!!openEditMode}
          setOpen={setOpenEditMode}
          editingItem={typeof openEditMode !== "boolean" ? openEditMode : null}
        />
      ) : null}
      {!!openDetailedContact ? (
        <DetailedContactModal
          handleDelete={handleDelete}
          open={!!openDetailedContact}
          contact={openDetailedContact}
          setOpen={setOpenDetailedContact}
          setOpenEditMode={setOpenEditMode}
        />
      ) : null}
    </Modal>
  );
};

export default ContactsModal;
